import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  FaMousePointer,
  FaBorderAll,
  FaGripLines,
  FaDoorOpen,
  FaThLarge,
  FaTrash,
  FaRedo,
  FaPen,
  FaExpand,
  FaCompress,
  FaUndo,
} from "react-icons/fa";

/**
 * Fabrika krokisi — masa/bölge + duvar/kapı/zemin çizimi.
 *
 * Olaylar TEK noktada (SVG kökü) toplanır ve hangi öğeye denk geldiği açık bir
 * hit-test ile bulunur. Şekillerin üzerine ayrı ayrı handler koymak, çizim
 * aracı seçiliyken tıklamanın sessizce ölmesine ve seçimin hiç oluşmamasına
 * yol açıyordu.
 *
 * Koordinat dönüşümü `getScreenCTM().inverse()` ile yapılır: viewBox oranı
 * kutunun oranından farklı olduğunda SVG letterbox'lanır, oransal hesap
 * imleçten sapar — bu yöntem her ölçek/oranda birebir doğrudur.
 *
 * Taşıma OPTİMİSTİKtir: bırakıldığı an yerel konum uygulanır, sunucu isteği
 * arkada gider (yanıt beklenirse şekil geri sıçrıyormuş gibi görünür).
 */
const VIEW_W = 1000;
const VIEW_H = 640;
const SNAP = 10;
/** Arka plan karesi. Kroki büyük ekranda gösterildiği için kareler de büyütüldü
 *  (20 → 40) ve çizgileri belirginleştirildi; küçük/soluk kareler ölçü almayı
 *  zorlaştırıyordu. SNAP değişmedi — yerleştirme hâlâ 10 birim hassasiyetinde. */
const GRID = 40;
const GRID_STROKE = 0.9;
const MIN = { zone: 40, wall: 8, door: 20, floor: 40 };
/** Köşe tutamağının SVG birimi cinsinden yakalama yarıçapı */
const HANDLE_R = 9;
const CORNERS = [
  { id: "nw", cursor: "cursor-nwse-resize" },
  { id: "ne", cursor: "cursor-nesw-resize" },
  { id: "sw", cursor: "cursor-nesw-resize" },
  { id: "se", cursor: "cursor-nwse-resize" },
];
const cornerPoint = (g, id) => ({
  x: id === "nw" || id === "sw" ? g.x : g.x + g.width,
  y: id === "nw" || id === "ne" ? g.y : g.y + g.height,
});
/** Tutamağın karşı köşesi — boyutlandırmada sabit kalan nokta */
const oppositeCorner = (g, id) =>
  cornerPoint(g, { nw: "se", ne: "sw", sw: "ne", se: "nw" }[id]);

// Tailwind sınıfları STATİK yazılır (dinamik `fill-${c}-400` purge'lenir)
const ZONE_COLORS = {
  emerald: { fill: "fill-emerald-400", stroke: "stroke-emerald-700", chip: "bg-emerald-400", text: "fill-emerald-950" },
  blue: { fill: "fill-blue-400", stroke: "stroke-blue-700", chip: "bg-blue-400", text: "fill-blue-950" },
  amber: { fill: "fill-amber-400", stroke: "stroke-amber-700", chip: "bg-amber-400", text: "fill-amber-950" },
  purple: { fill: "fill-purple-400", stroke: "stroke-purple-700", chip: "bg-purple-400", text: "fill-purple-950" },
  rose: { fill: "fill-rose-400", stroke: "stroke-rose-700", chip: "bg-rose-400", text: "fill-rose-950" },
  cyan: { fill: "fill-cyan-400", stroke: "stroke-cyan-700", chip: "bg-cyan-400", text: "fill-cyan-950" },
  slate: { fill: "fill-slate-400", stroke: "stroke-slate-700", chip: "bg-slate-400", text: "fill-slate-950" },
};
const ZONE_COLOR_KEYS = Object.keys(ZONE_COLORS);

const FLOOR_COLORS = {
  // Mimari plandaki gibi beyaz zemin + ince çerçeve. Kroki gerçek kat planından
  // kurulduğunda odalar dolgusuz görünsün diye eklendi (dolgulu kutular planla
  // yan yana konunca kalabalık duruyordu).
  white: { fill: "fill-white", stroke: "stroke-gray-400", chip: "bg-white border border-gray-300" },
  // ÇERÇEVESİZ beyaz: odanın sınırını zaten duvarlar çiziyor. Kutuya bir de
  // kendi çerçevesi konunca aynı yerde iki çizgi üst üste biniyor ve kalın/
  // bulanık görünüyordu — plandan çıkarılan odalar bunu kullanır.
  plain: { fill: "fill-white", stroke: "stroke-transparent", chip: "bg-white border border-dotted border-gray-300" },
  // SADECE YAZI: kutu çizilmez, yalnız etiketi görünür. Planda çerçevesi
  // olmayan ama adı yazan alanlar için (yukarı açık koridor, geçiş alanı) —
  // çerçeve çizilseydi planda olmayan bir duvar varmış gibi görünürdü.
  none: { fill: "fill-transparent", stroke: "stroke-transparent", chip: "bg-transparent border border-dashed border-gray-400" },
  stone: { fill: "fill-stone-200", stroke: "stroke-stone-400", chip: "bg-stone-300" },
  sky: { fill: "fill-sky-100", stroke: "stroke-sky-300", chip: "bg-sky-200" },
  lime: { fill: "fill-lime-100", stroke: "stroke-lime-300", chip: "bg-lime-200" },
  orange: { fill: "fill-orange-100", stroke: "stroke-orange-300", chip: "bg-orange-200" },
};
const FLOOR_COLOR_KEYS = Object.keys(FLOOR_COLORS);

const PATTERNS = [
  { id: "solid", label: "Düz" },
  { id: "hatch", label: "Tarama" },
  { id: "dots", label: "Nokta" },
  { id: "grid", label: "Kare" },
  { id: "tile", label: "Fayans" },
];

const TOOLS = [
  { id: "select", label: "Seç", icon: FaMousePointer },
  { id: "zone", label: "Masa", icon: FaBorderAll },
  { id: "wall", label: "Duvar", icon: FaGripLines },
  { id: "door", label: "Kapı", icon: FaDoorOpen },
  { id: "floor", label: "Zemin", icon: FaThLarge },
];

const KIND_LABEL = { wall: "Duvar", door: "Kapı", floor: "Zemin" };

const snap = (v) => Math.round(v / SNAP) * SNAP;
const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);
const rectsOverlap = (a, b) =>
  a.x < b.x + b.width && a.x + a.width > b.x && a.y < b.y + b.height && a.y + a.height > b.y;
const inside = (p, pos, w, h) =>
  p.x >= pos.x && p.x <= pos.x + w && p.y >= pos.y && p.y <= pos.y + h;

/**
 * Yapı öğesinin adını kutunun ortasına yazar (zemin ve kapı).
 *
 * `label` alanı modelde baştan beri var ama çizilmiyordu; bu yüzden krokideki
 * WC / soyunma odası / raf / asansör gibi çalışma bölgesi OLMAYAN alanlar
 * isimsiz gri kutular olarak duruyordu. Bunları "masa" (FactoryZone) yapmak
 * çözüm değil: o zaman bölge listesine düşer ve tuvalete tablet atanabilir
 * hale gelirdi. Duvara yazı basılmaz — duvar ince bir çizgi, yazı okunmaz.
 *
 * Kutuya sığsın diye kaba bir kelime sarması yapılır (SVG metni kendiliğinden
 * satır kırmaz); sığmayacak kadar küçük kutuda yazı hiç çizilmez.
 */
// ── Kroki yazıları ────────────────────────────────────────────────────────
// Tek bir yerleşim mantığı hem bölge (masa) hem yapı öğesi adlarını çizer:
// yazı kutuya SIĞDIRILIR — gerekirse satıra bölünür, puntosu küçültülür,
// ya da dar-uzun kutuda 90° çevrilir. Öncesinde punto sabitti ve bölge adı
// hiç satır kırmıyordu; "Çay Makinesi Montaj Hattı" kutusundan taşıyordu.

/** Harf başına yaklaşık genişlik (SVG'de metin ölçülemiyor; BÜYÜK harfli
 *  adlar geniş geldiği için bilerek cömert — dar tahmin taşmaya yol açar). */
const CHAR_RATIO = 0.62;
const LABEL_MIN_FONT = 5.5;
/** Kutunun kenarına yapışmasın: kullanılabilir alan oranı. */
const PAD_X = 0.88;
const PAD_Y = 0.86;

/** Metni satır başına en fazla `maxChars` olacak şekilde böler. */
const wrapTo = (text, maxChars) => {
  const lines = [];
  let cur = "";
  for (const w of String(text).split(/\s+/)) {
    if (!cur.length) cur = w;
    else if (cur.length + w.length + 1 <= maxChars) cur = `${cur} ${w}`;
    else {
      lines.push(cur);
      cur = w;
    }
  }
  if (cur.length) lines.push(cur);
  return lines;
};

/**
 * Kutuya sığan EN BÜYÜK yazı düzenini bulur.
 * Düz ve 90° çevrilmiş yerleşimi ayrı ayrı dener, hangisi daha büyük punto
 * veriyorsa onu seçer (eşitlikte düz kazanır — okuması kolay). Hiçbir punto
 * sığmıyorsa null döner ve yazı hiç çizilmez; taşmış yazı yerine boş kutu.
 */
const fitLabel = (text, boxW, boxH, maxFont) => {
  if (!text) return null;
  const W = boxW * PAD_X;
  const H = boxH * PAD_Y;
  let best = null;
  for (const rot of [false, true]) {
    const availW = rot ? H : W; // yazının uzadığı yön
    const availH = rot ? W : H; // satırların dizildiği yön
    for (let fs = maxFont; fs >= LABEL_MIN_FONT; fs -= 0.5) {
      const cw = fs * CHAR_RATIO;
      const lh = fs * 1.25;
      const lines = wrapTo(text, Math.max(1, Math.floor(availW / cw)));
      const widest = Math.max(...lines.map((l) => l.length)) * cw;
      if (widest <= availW && lines.length * lh <= availH) {
        if (!best || fs > best.fs) best = { fs, lh, lines, rot };
        break; // bu yönde bulunabilecek en büyük punto
      }
    }
  }
  return best;
};

/** Bulunan yerleşimi SVG metnine çevirir (çok satır + gerekiyorsa çevirme). */
const labelNode = (fit, cx, cy, className) => (
  <text
    x={cx}
    y={cy}
    textAnchor="middle"
    dominantBaseline="central"
    fontSize={fit.fs}
    transform={fit.rot ? `rotate(-90 ${cx} ${cy})` : undefined}
    className={`${className} pointer-events-none select-none`}
  >
    {fit.lines.map((ln, i) => (
      <tspan key={`${ln}-${i}`} x={cx} dy={i === 0 ? -((fit.lines.length - 1) * fit.lh) / 2 : fit.lh}>
        {ln}
      </tspan>
    ))}
  </text>
);

const itemLabelText = (it, pos, g) => {
  const fit = fitLabel(it.label, g.width, g.height, 9);
  if (!fit) return null;
  return labelNode(fit, pos.x + g.width / 2, pos.y + g.height / 2, "fill-gray-600 font-medium");
};

export default function FactoryMap({
  zones = [],
  layoutItems = [],
  kiosksByZone = {},
  canManage = false,
  /** Araç çubuğunun basılacağı DOM düğümü (sayfa başlığına taşımak için).
   *  Verilmezse eskisi gibi krokinin üstünde durur. */
  toolbarSlot = null,
  /** true → kroki kapsayıcısının BOYUNU doldurur (sabit 520px yerine). */
  fill = false,
  fullscreen = false,
  onToggleFullscreen,
  onDrawZone,
  onSelectZone,
  onEditZone,
  onMoveZone,
  onDeleteZone,
  onCreateItem,
  onUpdateItem,
  onDeleteItem,
}) {
  const svgRef = useRef(null);
  const [tool, setTool] = useState("select");
  const [zoneColor, setZoneColor] = useState("emerald");
  const [floorColor, setFloorColor] = useState("stone");
  const [floorPattern, setFloorPattern] = useState("hatch");
  const [drag, setDrag] = useState(null);
  const [selected, setSelected] = useState(null); // {type:"zone"|"item", id}
  /** Optimistik konumlar: sunucu yanıtını beklemeden anında uygulanır */
  const [overrides, setOverrides] = useState({});

  // ── Geri / İleri Al ─────────────────────────────────────────────────────
  // Kroki sürükleyerek düzenleniyor; yanlışlıkla oynatılan bir kutuyu elle
  // eski yerine koymak neredeyse imkânsız (piksel piksel). Her taşıma/boyut
  // değişikliği burada "yap/geri al" çifti olarak saklanır.
  const [past, setPast] = useState([]);
  const [futureSteps, setFutureSteps] = useState([]);

  /** Yeni bir adım kaydet — ileri geçmişi sıfırlar (klasik geri-al davranışı). */
  const pushStep = useCallback((step) => {
    setPast((p) => [...p.slice(-49), step]); // en fazla 50 adım
    setFutureSteps([]);
  }, []);

  /** Geometri değişimini hem ekranda hem sunucuda uygula. */
  const applyGeom = useCallback(
    (step, geom) => {
      setOverrides((o) => ({ ...o, [step.id]: geom }));
      if (step.type === "zone") {
        onMoveZone?.(step.el, {
          gridX: geom.x,
          gridY: geom.y,
          ...(geom.width != null ? { width: geom.width, height: geom.height } : {}),
        });
      } else {
        onUpdateItem?.(step.el, geom);
      }
    },
    [onMoveZone, onUpdateItem],
  );

  const undo = useCallback(() => {
    setPast((p) => {
      if (!p.length) return p;
      const step = p[p.length - 1];
      applyGeom(step, step.before);
      setFutureSteps((f) => [...f, step]);
      return p.slice(0, -1);
    });
  }, [applyGeom]);

  const redo = useCallback(() => {
    setFutureSteps((f) => {
      if (!f.length) return f;
      const step = f[f.length - 1];
      applyGeom(step, step.after);
      setPast((p) => [...p, step]);
      return f.slice(0, -1);
    });
  }, [applyGeom]);

  // Cmd/Ctrl+Z geri, Cmd/Ctrl+Shift+Z ileri. Yazı alanına odaklıyken karışmasın.
  useEffect(() => {
    if (!canManage) return undefined;
    const onKey = (e) => {
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "z") {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [canManage, undo, redo]);

  const placedZones = useMemo(
    () => zones.filter((z) => z.gridX != null && z.gridY != null && z.width && z.height),
    [zones]
  );
  const unplacedZones = useMemo(
    () => zones.filter((z) => z.isActive && (z.gridX == null || !z.width)),
    [zones]
  );
  // Çizim sırası: zemin altta → duvar → kapı
  const sortedItems = useMemo(() => {
    const order = { floor: 0, wall: 1, door: 2 };
    return [...layoutItems].sort((a, b) => (order[a.kind] ?? 3) - (order[b.kind] ?? 3));
  }, [layoutItems]);

  // Sunucu verisi optimistik konuma yetiştiyse override'ı bırak
  useEffect(() => {
    setOverrides((prev) => {
      if (!Object.keys(prev).length) return prev;
      const next = { ...prev };
      let changed = false;
      [...placedZones, ...layoutItems].forEach((el) => {
        const o = next[el._id];
        if (!o) return;
        const x = el.gridX ?? el.x;
        const y = el.gridY ?? el.y;
        const same = (k, v) => o[k] === undefined || o[k] === v;
        if (same("x", x) && same("y", y) && same("width", el.width) && same("height", el.height)) {
          delete next[el._id];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [placedZones, layoutItems]);

  /** Ekran koordinatı → SVG kullanıcı koordinatı (letterbox'a duyarlı, birebir) */
  const toSvg = useCallback((e) => {
    const svg = svgRef.current;
    const ctm = svg?.getScreenCTM();
    if (!ctm) return { x: 0, y: 0 };
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const p = pt.matrixTransform(ctm.inverse());
    return { x: clamp(p.x, 0, VIEW_W), y: clamp(p.y, 0, VIEW_H) };
  }, []);

  /** Öğenin güncel geometrisi — optimistik override varsa onun üstüne biner */
  const geomOf = useCallback(
    (el, isZone) => {
      const base = {
        x: isZone ? el.gridX : el.x,
        y: isZone ? el.gridY : el.y,
        width: el.width,
        height: el.height,
      };
      return overrides[el._id] ? { ...base, ...overrides[el._id] } : base;
    },
    [overrides]
  );

  /** İmlecin altındaki en üstteki öğe: masa → kapı → duvar → zemin */
  const hitTest = useCallback(
    (p) => {
      for (let i = placedZones.length - 1; i >= 0; i -= 1) {
        const z = placedZones[i];
        const gz = geomOf(z, true);
        if (inside(p, gz, gz.width, gz.height)) return { type: "zone", el: z };
      }
      for (let i = sortedItems.length - 1; i >= 0; i -= 1) {
        const it = sortedItems[i];
        const gi = geomOf(it, false);
        if (inside(p, gi, gi.width, gi.height)) return { type: "item", el: it };
      }
      return null;
    },
    [placedZones, sortedItems, geomOf]
  );

  // ── Sürükleme / seçim ──────────────────────────────────────────────────
  const onPointerDown = (e) => {
    const p = toSvg(e);

    if (!canManage) {
      const hit = hitTest(p);
      if (hit?.type === "zone") onSelectZone?.(hit.el);
      return;
    }

    svgRef.current?.setPointerCapture?.(e.pointerId);

    if (tool === "select") {
      // Önce seçili öğenin köşe tutamakları — üstlerinde boyutlandırma başlar
      const selEl = selectedZone || selectedItem;
      if (selEl) {
        const g = geomOf(selEl, Boolean(selectedZone));
        const corner = CORNERS.find((c) => {
          const cp = cornerPoint(g, c.id);
          return Math.abs(p.x - cp.x) <= HANDLE_R && Math.abs(p.y - cp.y) <= HANDLE_R;
        });
        if (corner) {
          setDrag({
            mode: "resize",
            type: selectedZone ? "zone" : "item",
            el: selEl,
            anchor: oppositeCorner(g, corner.id),
            rect: g,
            startGeom: g, // geri alma için başlangıç geometrisi
            moved: false,
          });
          return;
        }
      }

      const hit = hitTest(p);
      if (!hit) {
        setSelected(null);
        return;
      }
      const g = geomOf(hit.el, hit.type === "zone");
      setSelected({ type: hit.type, id: hit.el._id });
      setDrag({
        mode: "move",
        type: hit.type,
        el: hit.el,
        dx: p.x - g.x,
        dy: p.y - g.y,
        x: g.x,
        y: g.y,
        startGeom: g, // geri alma için başlangıç geometrisi
        moved: false,
      });
      return;
    }

    setDrag({ mode: "draw", kind: tool, x0: p.x, y0: p.y, x1: p.x, y1: p.y });
  };

  const onPointerMove = (e) => {
    if (!drag) return;
    const p = toSvg(e);
    if (drag.mode === "draw") {
      setDrag({ ...drag, x1: p.x, y1: p.y });
      return;
    }

    if (drag.mode === "resize") {
      const kind = drag.type === "zone" ? "zone" : drag.el.kind;
      const min = MIN[kind] || 10;
      const a = drag.anchor;
      const width = Math.max(snap(Math.abs(p.x - a.x)), kind === "wall" ? 4 : min);
      const height = Math.max(snap(Math.abs(p.y - a.y)), kind === "wall" ? 4 : min);
      const x = clamp(snap(Math.min(a.x, p.x)), 0, VIEW_W - width);
      const y = clamp(snap(Math.min(a.y, p.y)), 0, VIEW_H - height);
      setDrag({ ...drag, rect: { x, y, width, height }, moved: true });
      return;
    }

    const gm = geomOf(drag.el, drag.type === "zone");
    const nx = clamp(snap(p.x - drag.dx), 0, VIEW_W - gm.width);
    const ny = clamp(snap(p.y - drag.dy), 0, VIEW_H - gm.height);
    setDrag({ ...drag, x: nx, y: ny, moved: drag.moved || nx !== drag.x || ny !== drag.y });
  };

  const onPointerUp = (e) => {
    svgRef.current?.releasePointerCapture?.(e.pointerId);
    if (!drag) return;

    if (drag.mode === "draw") {
      const x = snap(Math.min(drag.x0, drag.x1));
      const y = snap(Math.min(drag.y0, drag.y1));
      const w = snap(Math.abs(drag.x1 - drag.x0));
      const h = snap(Math.abs(drag.y1 - drag.y0));
      const kind = drag.kind;
      setDrag(null);
      if (w < MIN[kind] && h < MIN[kind]) return;

      if (kind === "zone") {
        const collides = placedZones.some((z) =>
          rectsOverlap({ x, y, width: w, height: h }, geomOf(z, true))
        );
        if (collides) return;
        onDrawZone?.({ gridX: x, gridY: y, width: w, height: h, color: zoneColor });
      } else {
        onCreateItem?.({
          kind,
          x,
          y,
          width: Math.max(w, kind === "wall" ? 4 : MIN[kind]),
          height: Math.max(h, kind === "wall" ? 4 : MIN[kind]),
          color: kind === "floor" ? floorColor : "slate",
          pattern: kind === "floor" ? floorPattern : "solid",
        });
      }
      return;
    }

    if (drag.mode === "resize") {
      const { el, type, rect, moved, startGeom } = drag;
      setDrag(null);
      if (!moved) return;
      if (type === "zone") {
        const collides = placedZones.some(
          (z) => String(z._id) !== String(el._id) && rectsOverlap(rect, geomOf(z, true))
        );
        if (collides) return;
        setOverrides((o) => ({ ...o, [el._id]: rect }));
        onMoveZone?.(el, {
          gridX: rect.x,
          gridY: rect.y,
          width: rect.width,
          height: rect.height,
        });
      } else {
        setOverrides((o) => ({ ...o, [el._id]: rect }));
        onUpdateItem?.(el, rect);
      }
      pushStep({ type, id: el._id, el, before: startGeom, after: rect });
      return;
    }

    const { el, type, x, y, moved, startGeom } = drag;
    setDrag(null);
    if (!moved) return; // yalnız seçim — seçim zaten pointerdown'da yapıldı

    const eg = geomOf(el, type === "zone");
    if (type === "zone") {
      const collides = placedZones.some(
        (z) =>
          String(z._id) !== String(el._id) &&
          rectsOverlap({ x, y, width: eg.width, height: eg.height }, geomOf(z, true))
      );
      if (collides) return;
      setOverrides((o) => ({ ...o, [el._id]: { x, y } }));
      onMoveZone?.(el, { gridX: x, gridY: y });
    } else {
      setOverrides((o) => ({ ...o, [el._id]: { x, y } }));
      onUpdateItem?.(el, { x, y });
    }
    pushStep({
      type,
      id: el._id,
      el,
      before: startGeom,
      after: { x, y, width: eg.width, height: eg.height },
    });
  };

  /** Render geometrisi: sürükleme/boyutlandırma sırasında canlı değer */
  const liveGeom = (el, isZone) => {
    if (drag && String(drag.el?._id) === String(el._id)) {
      if (drag.mode === "move") return { ...geomOf(el, isZone), x: drag.x, y: drag.y };
      if (drag.mode === "resize") return drag.rect;
    }
    return geomOf(el, isZone);
  };

  const selectedZone =
    selected?.type === "zone"
      ? placedZones.find((z) => String(z._id) === String(selected.id))
      : null;
  const selectedItem =
    selected?.type === "item"
      ? layoutItems.find((i) => String(i._id) === String(selected.id))
      : null;

  const deleteSelected = useCallback(() => {
    if (selectedItem) onDeleteItem?.(selectedItem);
    else if (selectedZone) onDeleteZone?.(selectedZone);
    setSelected(null);
  }, [selectedItem, selectedZone, onDeleteItem, onDeleteZone]);

  // Delete tuşu — input'a yazarken tetiklenmesin
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Delete") return;
      const t = e.target?.tagName;
      if (t === "INPUT" || t === "TEXTAREA" || t === "SELECT") return;
      if (!canManage || !selected) return;
      deleteSelected();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [canManage, selected, deleteSelected]);

  const ghost =
    drag?.mode === "draw"
      ? {
          x: Math.min(drag.x0, drag.x1),
          y: Math.min(drag.y0, drag.y1),
          w: Math.abs(drag.x1 - drag.x0),
          h: Math.abs(drag.y1 - drag.y0),
          kind: drag.kind,
        }
      : null;

  const ghostCollides =
    ghost?.kind === "zone" &&
    placedZones.some((z) =>
      rectsOverlap({ x: ghost.x, y: ghost.y, width: ghost.w, height: ghost.h }, geomOf(z, true))
    );

  /** Seçim çerçevesi + köşe tutamakları — seçili olan net görünsün */
  const selectionOverlay = (pos, w, h, rx = 0) => (
    <g className="pointer-events-none">
      <rect
        x={pos.x - 3}
        y={pos.y - 3}
        width={w + 6}
        height={h + 6}
        rx={rx}
        fill="none"
        className="stroke-indigo-600"
        strokeWidth={2.5}
        strokeDasharray="6 4"
      />
      {CORNERS.map((c) => {
        const cp = cornerPoint({ x: pos.x, y: pos.y, width: w, height: h }, c.id);
        return (
          <rect
            key={c.id}
            x={cp.x - 4}
            y={cp.y - 4}
            width={8}
            height={8}
            className={`fill-white stroke-indigo-600 ${c.cursor}`}
            strokeWidth={1.5}
            style={{ pointerEvents: "auto" }}
          />
        );
      })}
    </g>
  );

  // Araç çubuğu ayrı değişkende: sayfa `toolbarSlot` verirse başlık şeridine
  // taşınır (krokiye daha çok yer kalsın diye), vermezse eski yerinde durur.
  const toolbar = canManage ? (
    <>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
            {TOOLS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTool(t.id)}
                title={t.label}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold border-r border-gray-200 last:border-r-0 transition-colors ${
                  tool === t.id ? "bg-indigo-50 text-indigo-700" : "bg-white text-gray-500 hover:bg-gray-50"
                }`}
              >
                <t.icon className="w-3 h-3" />
                <span className="hidden sm:inline">{t.label}</span>
              </button>
            ))}
          </div>

          {tool === "zone" && (
            <div className="inline-flex items-center gap-1.5">
              <span className="text-[11px] text-gray-400">Renk:</span>
              {ZONE_COLOR_KEYS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setZoneColor(c)}
                  className={`w-5 h-5 rounded-full ${ZONE_COLORS[c].chip} ${
                    zoneColor === c ? "ring-2 ring-offset-1 ring-gray-500" : ""
                  }`}
                />
              ))}
            </div>
          )}

          {tool === "floor" && (
            <div className="inline-flex items-center gap-1.5 flex-wrap">
              <span className="text-[11px] text-gray-400">Zemin:</span>
              {FLOOR_COLOR_KEYS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setFloorColor(c)}
                  className={`w-5 h-5 rounded-full ${FLOOR_COLORS[c].chip} ${
                    floorColor === c ? "ring-2 ring-offset-1 ring-gray-500" : ""
                  }`}
                />
              ))}
              <select
                value={floorPattern}
                onChange={(e) => setFloorPattern(e.target.value)}
                className="text-xs border border-input rounded-lg px-2 py-1"
              >
                {PATTERNS.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Geri / İleri Al — yanlışlıkla oynatılan kutuyu geri koymanın tek
              pratik yolu (elle piksel piksel yerine oturtmak mümkün değil). */}
          <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
            <button
              type="button"
              onClick={undo}
              disabled={past.length === 0}
              title="Geri al (⌘Z)"
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold border-r border-gray-200 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:hover:bg-white"
            >
              <FaUndo className="w-3 h-3" />
              <span className="hidden lg:inline">Geri</span>
            </button>
            <button
              type="button"
              onClick={redo}
              disabled={futureSteps.length === 0}
              title="İleri al (⌘⇧Z)"
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:hover:bg-white"
            >
              <FaUndo className="w-3 h-3 -scale-x-100" />
              <span className="hidden lg:inline">İleri</span>
            </button>
          </div>

          {onToggleFullscreen && (
            <button
              type="button"
              onClick={onToggleFullscreen}
              title={fullscreen ? "Tam ekrandan çık (Esc)" : "Tam ekran"}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-200 bg-white text-xs font-semibold text-gray-600 hover:bg-gray-50"
            >
              {fullscreen ? <FaCompress className="w-3 h-3" /> : <FaExpand className="w-3 h-3" />}
              <span className="hidden lg:inline">{fullscreen ? "Küçült" : "Tam ekran"}</span>
            </button>
          )}
        </div>
    </>
  ) : null;

  // Seçili öğe eylem çubuğu — artık krokinin ÜSTÜNDE yüzüyor. Araç çubuğu
  // sayfa başlığına taşınabildiği için oradan ayrıldı: seçim krokide yapılıyor,
  // eylemi de seçilen şeyin yanında durması gerekiyor.
  const selectionBar = (selectedZone || selectedItem) && (
    <div className="absolute top-2 right-2 z-10 inline-flex items-center gap-1.5 bg-indigo-50/95 backdrop-blur border border-indigo-200 rounded-lg px-2 py-1 shadow-sm">
      <span className="text-[11px] font-semibold text-indigo-700">
        {selectedZone ? selectedZone.name : KIND_LABEL[selectedItem.kind]}
      </span>
      {selectedZone && (
        <button
          type="button"
          onClick={() => onEditZone?.(selectedZone)}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
        >
          <FaPen className="w-2.5 h-2.5" /> Düzenle
        </button>
      )}
      {selectedItem?.kind === "door" && (
        <button
          type="button"
          onClick={() =>
            onUpdateItem?.(selectedItem, { rotation: ((selectedItem.rotation || 0) + 90) % 360 })
          }
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
        >
          <FaRedo className="w-2.5 h-2.5" /> Çevir
        </button>
      )}
      <button
        type="button"
        onClick={deleteSelected}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold bg-white border border-gray-300 text-red-500 hover:bg-red-50"
      >
        <FaTrash className="w-2.5 h-2.5" /> Sil
      </button>
    </div>
  );

  return (
    <div className={`flex flex-col gap-2 ${fill ? "h-full min-h-0" : ""}`}>
      {toolbar && (toolbarSlot ? createPortal(toolbar, toolbarSlot) : toolbar)}

      <div
        className={`relative rounded-xl border border-gray-200 bg-white overflow-hidden ${
          fill ? "flex-1 min-h-0" : ""
        }`}
      >
        {selectionBar}
        <svg
          ref={svgRef}
          viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
          preserveAspectRatio="xMidYMid meet"
          className={`select-none touch-none ${fill ? "w-full h-full block" : "w-full"} ${
            canManage ? (tool === "select" ? "cursor-default" : "cursor-crosshair") : "cursor-pointer"
          }`}
          style={fill ? undefined : { maxHeight: 520 }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          role="img"
          aria-label="Fabrika krokisi"
        >
          <defs>
            <pattern id="fm-hatch" width="8" height="8" patternUnits="userSpaceOnUse">
              <path d="M0,8 l8,-8" className="stroke-gray-400" strokeWidth="1" />
            </pattern>
            <pattern id="fm-dots" width="10" height="10" patternUnits="userSpaceOnUse">
              <circle cx="2" cy="2" r="1.2" className="fill-gray-400" />
            </pattern>
            <pattern id="fm-grid" width="12" height="12" patternUnits="userSpaceOnUse">
              <path d="M12,0 L0,0 0,12" fill="none" className="stroke-gray-400" strokeWidth="0.8" />
            </pattern>
            <pattern id="fm-tile" width="20" height="20" patternUnits="userSpaceOnUse">
              <rect width="20" height="20" fill="none" className="stroke-gray-400" strokeWidth="0.8" />
              <rect x="10" y="10" width="10" height="10" fill="none" className="stroke-gray-400" strokeWidth="0.5" />
            </pattern>
          </defs>

          <rect x={0} y={0} width={VIEW_W} height={VIEW_H} className="fill-gray-50" />
          {Array.from({ length: Math.floor(VIEW_W / GRID) - 1 }, (_, i) => (
            <line key={`v${i}`} x1={(i + 1) * GRID} y1={0} x2={(i + 1) * GRID} y2={VIEW_H} className="stroke-gray-300" strokeWidth={GRID_STROKE} />
          ))}
          {Array.from({ length: Math.floor(VIEW_H / GRID) - 1 }, (_, i) => (
            <line key={`h${i}`} x1={0} y1={(i + 1) * GRID} x2={VIEW_W} y2={(i + 1) * GRID} className="stroke-gray-300" strokeWidth={GRID_STROKE} />
          ))}

          {/* Yapı öğeleri */}
          {sortedItems.map((it) => {
            const g = liveGeom(it, false);
            const pos = { x: g.x, y: g.y };
            const isSel = selected?.type === "item" && String(selected.id) === String(it._id);

            if (it.kind === "floor") {
              const c = FLOOR_COLORS[it.color] || FLOOR_COLORS.stone;
              return (
                <g key={it._id}>
                  <rect x={pos.x} y={pos.y} width={g.width} height={g.height} className={`${c.fill} ${c.stroke}`} strokeWidth={1} />
                  {it.pattern !== "solid" && (
                    <rect x={pos.x} y={pos.y} width={g.width} height={g.height} fill={`url(#fm-${it.pattern})`} opacity={0.5} />
                  )}
                  {itemLabelText(it, pos, g)}
                  {isSel && selectionOverlay(pos, g.width, g.height)}
                </g>
              );
            }

            if (it.kind === "wall") {
              // `rotation` duvarda da geçerli: ince uzun bir duvar döndürülünce
              // ÇAPRAZ çizgi olur (merdiven çatısı, asansör çarpısı). Kroki
              // yalnız dikdörtgen çizebildiği için gerçek kat planındaki
              // çaprazlar başka türlü ifade edilemiyordu.
              const rot = it.rotation || 0;
              return (
                <g
                  key={it._id}
                  transform={rot ? `rotate(${rot} ${pos.x + g.width / 2} ${pos.y + g.height / 2})` : undefined}
                >
                  <rect x={pos.x} y={pos.y} width={g.width} height={g.height} className="fill-gray-500 stroke-gray-700" strokeWidth={1} />
                  {isSel && selectionOverlay(pos, g.width, g.height)}
                </g>
              );
            }

            const cx = pos.x + g.width / 2;
            const cy = pos.y + g.height / 2;
            const r = Math.min(g.width, g.height);
            return (
              <g key={it._id}>
                {/* Kapı = duvardaki BOŞLUK + açılma yayı (mimari gösterim).
                    Dikdörtgenin çerçevesi bilerek yok: planda kapının kutusu
                    çizilmez, beyaz dolgu duvarı keserek boşluğu oluşturur. */}
                <rect x={pos.x} y={pos.y} width={g.width} height={g.height} className="fill-white" />
                <g transform={`rotate(${it.rotation || 0} ${cx} ${cy})`}>
                  <path
                    d={`M ${pos.x} ${pos.y + g.height} L ${pos.x} ${pos.y + g.height - r} A ${r} ${r} 0 0 1 ${pos.x + r} ${pos.y + g.height}`}
                    fill="none"
                    className="stroke-gray-500"
                    strokeWidth={1.2}
                  />
                </g>
                {itemLabelText(it, pos, g)}
                {isSel && selectionOverlay(pos, g.width, g.height)}
              </g>
            );
          })}

          {/* Masalar — en üstte */}
          {placedZones.map((z) => {
            const g = liveGeom(z, true);
            const pos = { x: g.x, y: g.y };
            const moving = drag && String(drag.el?._id) === String(z._id);
            const kioskCount = (kiosksByZone[z._id] || []).length;
            const c = ZONE_COLORS[z.color] || ZONE_COLORS.emerald;
            const isSel = selected?.type === "zone" && String(selected.id) === String(z._id);

            return (
              <g key={z._id}>
                <rect
                  x={pos.x}
                  y={pos.y}
                  width={g.width}
                  height={g.height}
                  rx={4}
                  className={z.isActive ? `${c.fill} ${c.stroke}` : "fill-gray-300 stroke-gray-400"}
                  // Dolgu bilerek açık: kroki mimari bir çizim, doygun renk
                  // yanında kaba duruyordu. Çalışma alanı yine ayırt ediliyor
                  // ama çizgi işini bastırmıyor.
                  fillOpacity={moving ? 0.3 : 0.45}
                  strokeWidth={1.75}
                />
                {(() => {
                  const fit = fitLabel(z.name, g.width, g.height, 14);
                  return fit
                    ? labelNode(
                        fit,
                        pos.x + g.width / 2,
                        pos.y + g.height / 2,
                        `${z.isActive ? c.text : "fill-gray-600"} font-semibold`,
                      )
                    : null;
                })()}
                {kioskCount > 0 && (
                  <g className="pointer-events-none">
                    <circle cx={pos.x + g.width - 12} cy={pos.y + 12} r={9} className="fill-white stroke-gray-500" strokeWidth={1} />
                    <text x={pos.x + g.width - 12} y={pos.y + 12} textAnchor="middle" dominantBaseline="central" fontSize={10} className="fill-gray-700 font-bold">
                      {kioskCount}
                    </text>
                  </g>
                )}
                {isSel && selectionOverlay(pos, g.width, g.height, 5)}
              </g>
            );
          })}

          {/* Çizim hayaleti — çakışma varsa kırmızı */}
          {ghost && (ghost.w > 3 || ghost.h > 3) && (
            <rect
              x={ghost.x}
              y={ghost.y}
              width={ghost.w}
              height={ghost.h}
              rx={ghost.kind === "zone" ? 4 : 0}
              className={
                ghostCollides
                  ? "fill-red-300 stroke-red-600"
                  : ghost.kind === "wall"
                    ? "fill-gray-400 stroke-gray-600"
                    : "fill-indigo-300 stroke-indigo-600"
              }
              fillOpacity={0.4}
              strokeWidth={1.5}
              strokeDasharray="6 4"
            />
          )}
        </svg>
      </div>

      <div className="flex items-start justify-between gap-2 flex-wrap">
        <p className="text-[11px] text-gray-400 max-w-xl">
          {canManage
            ? tool === "select"
              ? "Tıkla → seç · sürükle → taşı · köşe tutamağından boyutlandır · üstteki çubuktan Düzenle / Çevir / Sil (veya Delete tuşu)"
              : "Boş alana basılı tutup sürükleyerek çiz. Masalar üst üste binemez — çakışan alan kırmızı görünür. Seçmek için Seç aracına dön."
            : "Bölgeye tıklayarak detayını görüntüleyebilirsin"}
        </p>
        {unplacedZones.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[11px] text-gray-400">Krokide yok:</span>
            {unplacedZones.map((z) => (
              <button
                key={z._id}
                onClick={() => onEditZone?.(z)}
                className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100"
              >
                {z.name}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export { ZONE_COLORS, ZONE_COLOR_KEYS };
